import React from 'react';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Icon from '@mui/material/Icon';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import TakePaymentModal from 'sectors/pilates/payments/modal/TakePaymentModal';

import AccountingDateFilterCard from '../components/AccountingDateFilterCard';
import AccountingExpenseTab from '../components/expense/AccountingExpenseTab';
import AddExpenseModal from '../components/expense/modal/AddExpenseModal';
import AccountingIncomeTab from '../components/income/AccountingIncomeTab';
import AccountingOverviewTab from '../components/overview/AccountingOverviewTab';
import AccountingPayrollTab from '../components/payroll/AccountingPayrollTab';
import AccountingTopTabs from '../components/AccountingTopTabs';
import { useAccountingPage } from '../hooks/useAccountingPage';
import { trMoney } from '../utils/accountingFormat';

export default function PilatesAccountingPage() {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';

  const surface = isDark ? alpha('#fff', 0.06) : alpha('#0b1220', 0.04);
  const surface2 = isDark ? alpha('#fff', 0.04) : alpha('#0b1220', 0.03);
  const border = isDark ? alpha('#fff', 0.1) : alpha('#0b1220', 0.1);
  const [takePaymentOpen, setTakePaymentOpen] = React.useState(false);
  const [addExpenseOpen, setAddExpenseOpen] = React.useState(false);

  const {
    topTab,
    setTopTab,
    q,
    setQ,
    onlyOverdue,
    setOnlyOverdue,
    datePreset,
    setDatePreset,
    dateRange,
    setDateRange,
    overview,
    income,
    expenses,
    payroll,
  } = useAccountingPage();

  const quickButtonSx = {
    height: 38,
    px: 1.6,
    borderRadius: '999px',
    textTransform: 'none',
    fontWeight: 800,
    boxShadow: 'none',
  };

  return (
    <MDBox sx={{ p: isMobile ? 1.25 : 2, display: 'grid', gap: 1.4 }}>
      <MDBox
        sx={{
          p: isMobile ? 1.3 : 1.7,
          borderRadius: '24px',
          border: `1px solid ${border}`,
          background: isDark
            ? `linear-gradient(135deg, ${alpha('#0891b2', 0.2)}, ${alpha('#111827', 0.84)})`
            : 'linear-gradient(135deg, rgba(8,145,178,0.12), rgba(255,255,255,0.98))',
          boxShadow: 'none',
          display: 'grid',
          gap: 1.2,
        }}
      >
        <MDBox
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', lg: 'center' },
            justifyContent: 'space-between',
            gap: 1.2,
            flexDirection: { xs: 'column', lg: 'row' },
          }}
        >
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold" sx={{ lineHeight: 1.05 }}>
              Muhasebe Merkezi
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              Tahsilat, gider ve maas akislarini tek bir merkezden sade ve net bicimde yonet.
            </MDTypography>
          </MDBox>

          <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <MDTypography
              variant="caption"
              sx={{ color: theme.palette.text.secondary, fontWeight: 800 }}
            >
              Hizli islemler
            </MDTypography>
            <Button
              variant="contained"
              startIcon={<Icon fontSize="small">payments</Icon>}
              onClick={() => setTakePaymentOpen(true)}
              sx={(muiTheme) => ({
                ...quickButtonSx,
                background: `linear-gradient(135deg, ${muiTheme.palette.success.main}, ${muiTheme.palette.success.dark})`,
                color: '#fff',
                '&:hover': {
                  background: `linear-gradient(135deg, ${muiTheme.palette.success.dark}, ${muiTheme.palette.success.main})`,
                  boxShadow: 'none',
                },
              })}
            >
              Odeme al
            </Button>
            <Button
              variant="outlined"
              startIcon={<Icon fontSize="small">receipt_long</Icon>}
              onClick={() => setAddExpenseOpen(true)}
              sx={(muiTheme) => ({
                ...quickButtonSx,
                borderColor: alpha(muiTheme.palette.error.main, 0.28),
                color: muiTheme.palette.error.main,
                background: alpha(muiTheme.palette.error.main, 0.08),
                '&:hover': {
                  borderColor: alpha(muiTheme.palette.error.main, 0.42),
                  background: alpha(muiTheme.palette.error.main, 0.12),
                  boxShadow: 'none',
                },
              })}
            >
              Gider ekle
            </Button>
            <Chip
              size="small"
              label={`${overview.pendingReceivablesCount || 0} bekleyen tahsilat`}
              sx={{ borderRadius: '999px', fontWeight: 800 }}
            />
            <Chip
              size="small"
              label={`${overview.pendingExpensesCount || 0} bekleyen gider`}
              sx={{ borderRadius: '999px', fontWeight: 800 }}
            />
            <Chip
              size="small"
              label={`${overview.payrollReadyCount || 0} maasa dahil personel`}
              sx={{ borderRadius: '999px', fontWeight: 800 }}
            />
            <Chip
              size="small"
              label={`${overview.recentPaymentsCount || 0} son alinan odeme`}
              sx={{ borderRadius: '999px', fontWeight: 800 }}
            />
          </MDBox>
        </MDBox>
        <MDBox
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.8,
            alignItems: 'center',
          }}
        >
          <Chip
            size="small"
            label={`Tahsil edilen ${trMoney(overview.totalReceivedTry)}`}
            sx={{ borderRadius: '999px', fontWeight: 800 }}
          />
          <Chip
            size="small"
            label={`Bekleyen tahsilat ${trMoney(overview.totalPendingIncomeTry)}`}
            sx={{ borderRadius: '999px', fontWeight: 800 }}
          />
          <Chip
            size="small"
            label={`Bekleyen gider ${trMoney(overview.totalExpensePendingTry)}`}
            sx={{ borderRadius: '999px', fontWeight: 800 }}
          />
          <Chip
            size="small"
            label={`Net nakit ${trMoney(overview.netCashFlowTry)}`}
            sx={{ borderRadius: '999px', fontWeight: 800 }}
          />
        </MDBox>
      </MDBox>

      <AccountingDateFilterCard
        datePreset={datePreset}
        setDatePreset={setDatePreset}
        dateRange={dateRange}
        setDateRange={setDateRange}
      />

      <MDBox
        sx={{
          p: 0.8,
          borderRadius: '20px',
          border: `1px solid ${border}`,
          bgcolor: surface,
        }}
      >
        <AccountingTopTabs value={topTab} onChange={setTopTab} />
      </MDBox>

      <MDBox>
        {topTab === 0 && (
          <AccountingOverviewTab
            surface={surface}
            border={border}
            data={overview}
            expenses={expenses}
            income={income}
            dateRange={dateRange}
          />
        )}

        {topTab === 1 && (
          <AccountingIncomeTab
            surface={surface}
            surface2={surface2}
            border={border}
            q={q}
            setQ={setQ}
            onlyOverdue={onlyOverdue}
            setOnlyOverdue={setOnlyOverdue}
            data={income}
          />
        )}

        {topTab === 2 && (
          <AccountingExpenseTab
            surface={surface}
            surface2={surface2}
            border={border}
            q={q}
            setQ={setQ}
            data={expenses}
          />
        )}

        {topTab === 3 && <AccountingPayrollTab surface={surface} border={border} data={payroll} />}
      </MDBox>

      <TakePaymentModal open={takePaymentOpen} onClose={() => setTakePaymentOpen(false)} />

      <AddExpenseModal
        open={addExpenseOpen}
        onClose={() => setAddExpenseOpen(false)}
        onSubmit={async (payload) => {
          await expenses.createExpense(payload);
          setAddExpenseOpen(false);
          setTopTab(2);
        }}
      />
    </MDBox>
  );
}
